// Single-source wrapper around Google Analytics 4 (gtag.js).
//
// Why a wrapper instead of calling `window.gtag(...)` directly:
//   - One place to swap providers (GA → Plausible → Posthog) without
//     touching twenty event sites.
//   - Domain helpers map our booking funnel onto GA4's standard
//     ecommerce events so the GA reports light up correctly — no
//     custom dashboard work needed for the funnel view.
//   - Safe no-op when `VUE_APP_GA_MEASUREMENT_ID` is unset, so dev
//     and preview builds don't ping production analytics.
//
// Tags `funnel_step` on every event we send. Sequence:
//   1 search_initiated      → user clicked "Search trips"
//   2 view_search_results   → schedule list loaded
//   3 select_item           → a bus was tapped
//   4 select_sub_route      → a sub-route picked
//   5 select_seat           → seat selected on the seat map
//   6 begin_checkout        → seats successfully held
//   7 add_payment_info      → payment method chosen + form submitted
//   8 purchase              → booking confirmed (status: issued)
//
// Drop-off helpers:
//   - `markFurthestStep(n)` writes the latest step to sessionStorage so
//     a `booking_abandoned` event on `beforeunload` can report it.

const SESSION_FURTHEST_KEY = "abs_furthest_step";

let initialised = false;
let lastTrackedPath = "";

function gtagReady() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Bootstrap GA4 once per session. Call from main.js. Reads the
 * measurement id from the env at build time so dev / preview can
 * leave it unset and analytics becomes a silent no-op.
 */
export function initAnalytics() {
  if (initialised) return;
  const id = process.env.VUE_APP_GA_MEASUREMENT_ID;
  if (!id) return;
  if (!gtagReady()) {
    // The base snippet in index.html stubs window.gtag on load — if
    // we don't see it, the snippet isn't there. Bail rather than
    // throwing.
    return;
  }
  // `send_page_view: false` because we drive page views ourselves
  // from the router so SPA route changes get tracked too.
  window.gtag("config", id, { send_page_view: false });
  initialised = true;
  // Fire the initial page view AFTER config so the first hit lands
  // with the right `client_id`.
  pageView(window.location.pathname + window.location.search, document.title);
  // Wire the abandon listener once.
  window.addEventListener("beforeunload", () => {
    const step = sessionStorage.getItem(SESSION_FURTHEST_KEY);
    if (step) {
      // sendBeacon is the only thing the browser guarantees during
      // unload; gtag uses it under the hood when `transport: 'beacon'`
      // is set on the event.
      event("booking_abandoned", {
        furthest_step: Number(step),
        transport: "beacon",
      });
    }
  });
}

/** Track a virtual SPA page view. Idempotent on the same path. */
export function pageView(path, title) {
  if (!gtagReady()) return;
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.origin + path,
  });
}

/** Generic event helper. Prefer the domain helpers below. */
export function event(name, params = {}) {
  if (!gtagReady()) return;
  window.gtag("event", name, params);
}

/**
 * Remember the furthest step the user reached so a later abandon
 * event can name it. Monotonic — never moves backward.
 */
function markFurthestStep(step) {
  try {
    const prev = Number(sessionStorage.getItem(SESSION_FURTHEST_KEY) || 0);
    if (step > prev) sessionStorage.setItem(SESSION_FURTHEST_KEY, String(step));
  } catch { /* ignore quota */ }
}

/** Step 1 — `Search trips` clicked on the hero search card. */
export function trackSearchInitiated({ fromName, toName, date, passengers, tripType }) {
  markFurthestStep(1);
  event("search_initiated", {
    funnel_step: 1,
    origin: fromName,
    destination: toName,
    departure_date: date,
    passengers,
    trip_type: tripType,
  });
}

/** Step 2 — schedule list loaded. */
export function trackSearchResults({ fromName, toName, date, resultsCount }) {
  markFurthestStep(2);
  event("view_search_results", {
    funnel_step: 2,
    origin: fromName,
    destination: toName,
    departure_date: date,
    results: resultsCount,
  });
  // Standard ecommerce event — feeds into GA's "View item list" report.
  event("view_item_list", {
    item_list_id: "search_results",
    item_list_name: `${fromName} → ${toName} · ${date}`,
    items: [],
  });
}

/** Step 3 — bus tapped from the results list. */
export function trackBusSelected({ scheduleId, subRouteId, fare, fromName, toName }) {
  markFurthestStep(3);
  event("select_item", {
    funnel_step: 3,
    item_list_id: "search_results",
    items: [{
      item_id: String(scheduleId),
      item_name: `${fromName} → ${toName}`,
      item_variant: String(subRouteId),
      price: Number(fare) || 0,
      quantity: 1,
    }],
  });
}

/** Step 4 — sub-route picked (only present on the legacy two-step path). */
export function trackSubRouteSelected({ subRouteId, fare }) {
  markFurthestStep(4);
  event("select_sub_route", {
    funnel_step: 4,
    sub_route_id: subRouteId,
    price: Number(fare) || 0,
  });
}

/** Step 5 — seat selected on the seat map. */
export function trackSeatSelected({ scheduleId, seatId, totalSeats, totalFare }) {
  markFurthestStep(5);
  event("select_seat", {
    funnel_step: 5,
    schedule_id: scheduleId,
    seat_id: seatId,
    seats_selected: totalSeats,
    running_total: totalFare,
  });
}

/** Step 6 — seats successfully held; user pushed to passenger detail. */
export function trackBeginCheckout({ holdToken, scheduleId, subRouteId, totalFare, seatCount, currency = "TZS" }) {
  markFurthestStep(6);
  event("begin_checkout", {
    funnel_step: 6,
    currency,
    value: Number(totalFare) || 0,
    hold_token: holdToken,
    schedule_id: scheduleId,
    sub_route_id: subRouteId,
    seat_count: seatCount,
  });
}

/** Step 7 — payment method picked + form submitted. */
export function trackAddPaymentInfo({ paymentMethod, totalFare, seatCount, currency = "TZS" }) {
  markFurthestStep(7);
  event("add_payment_info", {
    funnel_step: 7,
    currency,
    value: Number(totalFare) || 0,
    payment_type: paymentMethod,
    seat_count: seatCount,
  });
}

/** Step 8 — booking confirmed (status: issued). */
export function trackPurchase({ bookingRef, totalFare, seatCount, currency = "TZS", paymentMethod }) {
  markFurthestStep(8);
  event("purchase", {
    funnel_step: 8,
    transaction_id: bookingRef,
    currency,
    value: Number(totalFare) || 0,
    payment_type: paymentMethod,
    items: [{ item_id: bookingRef, quantity: Number(seatCount) || 1 }],
  });
  // Clear furthest-step on a successful purchase so the abandon
  // handler doesn't double-fire when the user closes the tab after.
  try { sessionStorage.removeItem(SESSION_FURTHEST_KEY); } catch { /* */ }
}

/** Payment-gateway rejection. Funnel step stays where it was. */
export function trackPaymentFailed({ bookingRef, reason }) {
  event("payment_failed", { booking_ref: bookingRef, reason: reason || "unknown" });
}

/** A non-2xx response from any v2 endpoint, surfaced for debugging. */
export function trackApiError({ endpoint, status, message }) {
  event("api_error", { endpoint, status, message: message || "" });
}
